import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { FileDown, Gift, Printer, RefreshCw, Search, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import ReferralLeaderboard from "@/components/ReferralLeaderboard";
import EmailDomainStatus from "@/components/EmailDomainStatus";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND, errMsg, inr } from "@/lib/api";
import { STATUS_LABEL } from "@/pages/OrderPage";

const FILTERS = [["", "All"], ["paid", "Paid"], ["sent_to_printer", "At printer"], ["printing", "Printing"], ["shipped", "Shipped"], ["delivered", "Delivered"], ["fulfilment_error", "Needs attention"]];
const TONE = { delivered: "bg-[#F0F5F1] text-[#4F7A5C]", shipped: "bg-[#EAF0FF] text-[#3B5BDB]", fulfilment_error: "bg-rose-50 text-rose-700" };

function ShipForm({ o, onDone, couriers }) {
  const [awb, setAwb] = useState("");
  const [carrier, setCarrier] = useState(couriers[0] || "Delhivery");
  const [busy, setBusy] = useState(false);
  const ship = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${o.id}/status`, { status: "shipped", tracking: awb ? { carrier, awb } : null });
      toast.success("Marked shipped — customer notified on WhatsApp");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`ship-form-${o.id}`}>
      <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid={`ship-carrier-${o.id}`} className="h-9 rounded-md border border-[#E8E2D9] bg-white px-2 text-xs">
        {couriers.map((c) => <option key={c}>{c}</option>)}
        <option value="Other">Other courier</option>
      </select>
      <Input value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="AWB / tracking no." data-testid={`ship-awb-${o.id}`} className="h-9 w-40 bg-white text-xs" />
      <button onClick={ship} disabled={busy} data-testid={`mark-shipped-${o.id}`} className="btn-primary !py-2 !px-3 !text-xs"><Truck className="h-3.5 w-3.5" />Mark shipped</button>
    </div>
  );
}

function NoteBox({ o }) {
  const [note, setNote] = useState(o.admin_note || "");
  const [saved, setSaved] = useState(o.admin_note || "");
  const save = async () => {
    if (note.trim() === saved) return;
    try { const { data } = await api.patch(`/admin/orders/${o.id}/note`, { note }); setSaved(data.admin_note); setNote(data.admin_note); toast.success("Note saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <textarea value={note} onChange={(e) => setNote(e.target.value)} onBlur={save} rows={2} maxLength={500} placeholder="Private note (e.g. asked for gift wrap)" data-testid={`admin-note-${o.id}`}
      className={`mt-1 w-full resize-none rounded-md border px-2.5 py-1.5 text-xs leading-snug outline-none transition-colors focus:border-[#3B5BDB] ${saved ? "border-[#F3D36B] bg-[#FFF9E0]" : "border-[#E8E2D9] bg-white"}`} />
  );
}

function Row({ o, statuses, reload, couriers, me }) {
  const digital = o.kind === "caricature" && o.format === "digital";
  const setStatus = async (status) => {
    try { await api.patch(`/admin/orders/${o.id}/status`, { status }); toast.success(`Now "${STATUS_LABEL[status]}"`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove this ${o.is_test ? "test" : ""} order (${o.book_title})? This cannot be undone.`)) return;
    try { await api.delete(`/admin/orders/${o.id}`); toast.success("Order removed"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const removable = o.is_test || o.customer.email === me;
  const resubmit = async () => {
    try { await api.post(`/admin/orders/${o.id}/resubmit`); toast.success("Sent to the printer again"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr className="border-t border-[#E8E2D9] align-top" data-testid={`admin-order-row-${o.id}`}>
      <td className="px-4 py-3 text-xs text-[#8C8498] whitespace-nowrap">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}<br /><span className="font-mono">{o.id.slice(-6)}</span></td>
      <td className="px-4 py-3"><p className="font-medium" data-testid={`admin-order-customer-${o.id}`}>{o.customer.name || "—"}</p><p className="text-xs text-[#8C8498]">{o.customer.phone || o.customer.email}</p>{o.address?.city && <p className="text-xs text-[#8C8498]">{o.address.city}, {o.address.pincode}</p>}</td>
      <td className="px-4 py-3"><p>{o.book_title}</p><p className="text-xs text-[#8C8498]">{o.kind === "caricature" ? (digital ? "Digital poster" : "A3 poster") : `${o.size} · ${o.pages} pages`}{o.pricing.promo && ` · ${o.pricing.promo}`}</p><NoteBox o={o} /></td>
      <td className="px-4 py-3 font-semibold whitespace-nowrap">{inr(o.pricing.total)}</td>
      <td className="px-4 py-3">
        <span data-testid={`admin-order-status-${o.id}`} className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE[o.status] || "bg-[#F5F0E8] text-[#5E5868]"}`}>{STATUS_LABEL[o.status] || o.status}</span>
        {o.is_test && <span data-testid={`admin-order-test-${o.id}`} title="Placed with mock or Razorpay test-mode payment — no real money" className="ml-1.5 rounded-full border border-dashed border-[#C9A227] bg-[#FFF9E0] px-2.5 py-0.5 text-xs font-semibold text-[#8A6D00]">TEST</span>}
        {o.gift_wrap && <span data-testid={`admin-order-giftwrap-${o.id}`} className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-[#FCE4EC] px-2.5 py-0.5 text-xs font-medium text-[#C2185B]"><Gift className="h-3 w-3" />Gift wrap</span>}
        {o.gift_message && <p className="mt-1.5 max-w-[220px] rounded-md border border-[#F3C4D4] bg-[#FFF5F8] px-2 py-1 text-[11px] italic leading-snug text-[#8A2E52]" data-testid={`admin-order-giftmsg-${o.id}`}>“{o.gift_message}”</p>}
        {o.tracking?.awb && <p className="mt-1 text-xs text-[#8C8498]">{o.tracking.carrier} {o.tracking.awb}{o.tracking.url && <> · <a href={o.tracking.url} target="_blank" rel="noreferrer" className="text-[#3B5BDB] underline" data-testid={`admin-track-${o.id}`}>track</a></>}</p>}
        {o.fulfilment_note && <p className="mt-1.5 max-w-[240px] rounded-md bg-rose-50 px-2 py-1 text-[11px] leading-snug text-rose-700" data-testid={`admin-order-fulfilnote-${o.id}`}>{o.fulfilment_note}</p>}
        {o.provider_order_id && o.provider_info && !o.provider_info.mocked && <p className="mt-1 text-[11px] text-[#8C8498]" data-testid={`admin-order-provider-${o.id}`}>{o.provider_info.name} · {o.provider_order_id}</p>}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col items-start gap-2">
          <a href={`${BACKEND}/api/admin/orders/${o.id}/receipt`} target="_blank" rel="noreferrer" data-testid={`admin-receipt-${o.id}`} className="btn-ghost !py-1.5 !px-3 !text-xs"><FileDown className="h-3.5 w-3.5" />Receipt</a>
          {Object.keys(o.pdf_paths || {}).map((k) => (
            <a key={k} href={`${BACKEND}/api/admin/orders/${o.id}/pdf/${k}`} data-testid={`admin-pdf-${k}-${o.id}`} className="btn-primary !py-1.5 !px-3 !text-xs"><Printer className="h-3.5 w-3.5" />{k === "interior" ? "Pages PDF" : k === "cover" ? "Cover PDF" : "Poster PDF"}</a>
          ))}
          {!digital && ["pdf_ready", "fulfilment_error"].includes(o.status) && (
            <button onClick={resubmit} data-testid={`admin-resubmit-${o.id}`} className="btn-ghost !py-1.5 !px-3 !text-xs"><RefreshCw className="h-3.5 w-3.5" />Resubmit to printer</button>
          )}
          {!digital && o.status !== "delivered" && (
            <>
              {o.status !== "shipped" && <ShipForm o={o} onDone={reload} couriers={couriers} />}
              <select value="" onChange={(e) => e.target.value && setStatus(e.target.value)} data-testid={`admin-status-select-${o.id}`} className="h-9 rounded-md border border-[#E8E2D9] bg-white px-2 text-xs">
                <option value="">Set status…</option>
                {statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </>
          )}
          {removable && <button onClick={remove} data-testid={`admin-remove-order-${o.id}`} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:underline"><Trash2 className="h-3.5 w-3.5" />Remove {o.is_test ? "test order" : "my order"}</button>}
        </div>
      </td>
    </tr>
  );
}

export default function AdminOrders() {
  const { user } = useAuth();
  const [data, setData] = useState({ orders: [], counts: {}, statuses: [] });
  const [couriers, setCouriers] = useState([]);
  useEffect(() => { api.get("/config").then((r) => setCouriers(r.data.couriers || [])); }, []);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const load = () => api.get(`/admin/orders${filter ? `?status=${filter}` : ""}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (user?.role === "admin") load(); }, [user, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  const needle = q.trim().toLowerCase();
  const rows = data.orders.filter((o) => !needle || [o.book_title, o.customer.name, o.customer.email, o.customer.phone, o.id, o.tracking?.awb, o.admin_note].some((v) => (v || "").toLowerCase().includes(needle)));
  const total = Object.values(data.counts).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="mx-auto max-w-7xl px-5 py-12 sm:px-8" data-testid="admin-orders-page">
        <EmailDomainStatus />
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><p className="overline">Admin</p><h1 className="mt-2 font-serif text-4xl tracking-tight">Orders <span className="text-[#8C8498]">({total})</span></h1></div>
          <Link to="/admin/promos" data-testid="admin-orders-promos-link" className="text-sm text-[#3B5BDB] underline-offset-4 hover:underline">Promo codes →</Link>
          <Link to="/admin/print" data-testid="admin-orders-print-link" className="text-sm text-[#3B5BDB] underline-offset-4 hover:underline">Print partner →</Link>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="admin-order-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k || "all"}`} aria-pressed={filter === k}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${filter === k ? "bg-[#26232E] text-white" : "bg-white text-[#5E5868] hover:bg-[#F5F0E8]"}`}>
              {l}{k && data.counts[k] ? ` · ${data.counts[k]}` : ""}
            </button>
          ))}
          <div className="relative ml-auto"><Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-[#8C8498]" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, AWB…" data-testid="admin-order-search" className="h-9 w-64 bg-white pl-9 text-sm" /></div>
        </div>
        <div className="paper mt-6 overflow-x-auto rounded-lg">
          <table className="w-full text-sm" data-testid="admin-orders-table">
            <thead className="text-left text-xs uppercase tracking-wider text-[#8C8498]"><tr>{["Date", "Customer", "Item", "Amount", "Status", "Actions"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((o) => <Row key={o.id} o={o} statuses={data.statuses} reload={load} couriers={couriers} me={user?.email} />)}
              {!rows.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#8C8498]" data-testid="admin-orders-empty">No orders here yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <ReferralLeaderboard />
      </main>
    </div>
  );
}
